import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowLeft, FiSave, FiCheck } = FiIcons;

const FONTS = [
  'System Default',
  'Inter',
  'Poppins',
  'Nunito',
  'Raleway',
  'DM Sans',
  'Roboto',
  'Lato',
  'Open Sans',
  'Source Sans 3',
  'Outfit',
  'Quicksand',
  'Comfortaa',
  'Space Grotesk',
  'Montserrat',
  'Oswald',
  'Playfair Display',
  'Merriweather',
  'Libre Baskerville',
  'Exo',
  'Permanent Marker',
];

const FontField = ({ value, onChange }) => {
  const isCustom = value && !FONTS.includes(value);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {FONTS.map(f => (
          <button
            key={f}
            type="button"
            onClick={() => onChange(f)}
            className={`px-3 py-2 rounded-lg border text-sm text-left transition-all ${
              value === f
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
            }`}
            style={{ fontFamily: f === 'System Default' ? 'inherit' : `"${f}", sans-serif` }}
          >
            {f}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Or type any Google Font name</label>
        <input
          type="text"
          placeholder="e.g. Cabin, Josefin Sans…"
          value={isCustom ? value : ''}
          onChange={e => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      {value && value !== 'System Default' && (
        <p className="text-sm text-gray-500 italic" style={{ fontFamily: `"${value}", sans-serif` }}>
          Preview: The quick brown fox jumps over the lazy dog
        </p>
      )}
    </div>
  );
};

const ColourField = ({ label, hint, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value || '#4f46e5'}
        onChange={e => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer border border-gray-200 p-0.5 bg-white"
      />
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder="#4f46e5"
        className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <span className="w-8 h-8 rounded-full border border-gray-200 flex-shrink-0" style={{ backgroundColor: value || '#4f46e5' }} />
    </div>
  </div>
);

const Field = ({ label, hint, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    {type === 'textarea' ? (
      <textarea
        rows={3}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    )}
  </div>
);

const ImagePreview = ({ url }) => {
  const [err, setErr] = useState(false);
  useEffect(() => setErr(false), [url]);
  if (!url || err) return null;
  return (
    <img
      src={url}
      alt="preview"
      onError={() => setErr(true)}
      className="mt-2 h-16 object-contain rounded border border-gray-100"
    />
  );
};

const SiteSettings = () => {
  const navigate = useNavigate();
  const { isSuperuser } = useAuth();
  const { settings, reload } = useSettings();

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({ ...settings });
  }, [settings]);

  if (!isSuperuser) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center text-gray-500">
        You need superuser access to manage site settings.
      </div>
    );
  }

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      await reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-gray-600">
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Site Settings</h1>
          <p className="text-gray-500 mt-1 text-sm">Customise the app name, logo and homepage content</p>
        </div>
      </div>

      <div className="space-y-8">

        {/* App Identity */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">App Identity</h2>
          <Field label="App name" hint="Shown in the navbar and browser tab" value={form.app_name || ''} onChange={set('app_name')} />
          <div>
            <Field label="Navbar logo URL" hint="Small square logo in the top-left header" value={form.app_logo_url || ''} onChange={set('app_logo_url')} />
            <ImagePreview url={form.app_logo_url} />
          </div>
        </section>

        {/* Typography */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">Typography</h2>
          <p className="text-xs text-gray-400">Select a font for the whole site. Each option is rendered in its own typeface so you can see how it looks.</p>
          <FontField value={form.brand_font || 'System Default'} onChange={set('brand_font')} />
        </section>

        {/* Homepage */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">Homepage</h2>
          <div>
            <Field label="Centre logo URL" hint="Large logo image shown in the middle section of the homepage" value={form.home_logo_url || ''} onChange={set('home_logo_url')} />
            <ImagePreview url={form.home_logo_url} />
          </div>
          <Field label="Hero title" value={form.home_hero_title || ''} onChange={set('home_hero_title')} />
          <Field label="Hero body text" type="textarea" value={form.home_hero_body || ''} onChange={set('home_hero_body')} />
        </section>

        {/* Footer */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">Footer</h2>
          <Field label="Organisation name" value={form.footer_org_name || ''} onChange={set('footer_org_name')} />
          <Field label="Tagline" type="textarea" value={form.footer_tagline || ''} onChange={set('footer_tagline')} />
          <Field label="Address" hint="Displayed as a single line" value={form.footer_address || ''} onChange={set('footer_address')} />
          <Field label="Phone" value={form.footer_phone || ''} onChange={set('footer_phone')} />
          <Field label="Email" value={form.footer_email || ''} onChange={set('footer_email')} />
          <Field label="Map link URL" hint="Google Maps or similar" value={form.footer_map_url || ''} onChange={set('footer_map_url')} />
          <div>
            <Field label="Map image URL" hint="Preview photo shown in the Find Us box" value={form.footer_map_img || ''} onChange={set('footer_map_img')} />
            <ImagePreview url={form.footer_map_img} />
          </div>
        </section>

        {/* Brand Colours */}
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">Brand Colours</h2>
          <p className="text-xs text-gray-400">Changes apply instantly across the public site after saving. Admin pages always stay in indigo.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <ColourField label="Primary colour" hint="Buttons, links, active states" value={form.brand_primary || ''} onChange={set('brand_primary')} />
            <ColourField label="Primary dark" hint="Button hover state" value={form.brand_primary_dark || ''} onChange={set('brand_primary_dark')} />
            <ColourField label="Primary light" hint="Light tint backgrounds" value={form.brand_primary_light || ''} onChange={set('brand_primary_light')} />
            <ColourField label="Accent" hint="Icons, badges" value={form.brand_accent || ''} onChange={set('brand_accent')} />
            <ColourField label="Navbar background" value={form.brand_nav_bg || ''} onChange={set('brand_nav_bg')} />
            <ColourField label="Footer background" value={form.brand_footer_bg || ''} onChange={set('brand_footer_bg')} />
            <ColourField label="Footer text colour" value={form.brand_footer_text || ''} onChange={set('brand_footer_text')} />
          </div>
        </section>

        {/* Tiles */}
        {[1, 2, 3].map(n => (
          <section key={n} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-3">Top Tile {n}</h2>
            <Field label="Label" value={form[`tile_${n}_label`] || ''} onChange={set(`tile_${n}_label`)} />
            <Field label="Link URL" hint="Where this tile links to (external)" value={form[`tile_${n}_url`] || ''} onChange={set(`tile_${n}_url`)} />
            <div>
              <Field label="Image URL" hint="Background photo for this tile" value={form[`tile_${n}_img`] || ''} onChange={set(`tile_${n}_img`)} />
              <ImagePreview url={form[`tile_${n}_img`]} />
            </div>
          </section>
        ))}

      </div>

      {error && <p className="mt-6 text-sm text-red-600">{error}</p>}

      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60"
        >
          <SafeIcon icon={saved ? FiCheck : FiSave} className="w-4 h-4" />
          {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default SiteSettings;
